// React and hooks
import React, { useContext } from "react";
import "react-phone-input-2/lib/style.css";

// Contexts
import ScreenContext from "contexts/screen";

// Components
import {
  TitleBar,
  PrimaryButton,
  Visualization,
  Loading,
  NormalText,
  Modal,
  ModalContent,
} from "components/index";

// Utilities
import { formatProcedureName } from "utils/formatProcedureName";

// API types
import {
  FormNameType,
  ProcedureType,
  TFormName,
  TScreen,
  UploadTypeType,
} from "api/type";
import {
  TPostTransformsBatchResponse,
  TPostTransformsResponse,
} from "api/clinicos";

// Constants
import { adjustTextMap } from "api/constants";
import { mixpanelTrack } from "utils/mixpanel";
import { isAISupportedProcedure } from "utils/isAISuportedProcedureAndArea";
import { ClinicContext, SessionContext } from "contexts";
import { callFormSubmissionTracking } from "utils/callClinicosTracking";

type ScreenVisualizeProps = {
  batchId: string | null;
  isFormSubmitted: boolean;
  handleSetIsFormSubmittedTrue: (value: boolean) => void;
  handleSaveImageUrlOrBlob: (imageUrlOrBlob: string | Blob | null) => void;
  selectedProcedure: ProcedureType | null;
  postTransformsResponse: TPostTransformsResponse | null;
  postTransformsBatchResponse: TPostTransformsBatchResponse | null;
  uploadType: UploadTypeType;
  showTryAnotherModelButton?: boolean;
};

const ScreenVisualize: React.FC<ScreenVisualizeProps> = ({
  batchId,
  isFormSubmitted,
  handleSetIsFormSubmittedTrue,
  handleSaveImageUrlOrBlob,
  selectedProcedure,
  postTransformsResponse,
  postTransformsBatchResponse,
  uploadType,
  showTryAnotherModelButton = true,
}) => {
  // Context
  const clinic = useContext(ClinicContext);
  const session = useContext(SessionContext);
  const setScreen = useContext(ScreenContext)[1];

  // State
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleSetErrorMessage = (message: string) => {
    setErrorMessage(message);
  };

  // Effects
  React.useEffect(() => {
    if (!isFormSubmitted) {
      setIsModalOpen(true);
    }
  }, [isFormSubmitted]);

  const renderVisualization = () => {
    if (!selectedProcedure) return;
    if (!postTransformsResponse && !postTransformsBatchResponse) {
      return (
        <div
          style={{
            padding: "50px",
          }}
        >
          <Loading />
        </div>
      );
    }
    if (postTransformsResponse) {
      if (!postTransformsResponse.metadata.success) {
        return (
          <NormalText textContent={postTransformsResponse.metadata.reason} />
        );
      }
      return (
        <Visualization
          afterImageUrls={postTransformsResponse.download_urls}
          procedureDisplayMessage={adjustTextMap[selectedProcedure]}
          metaData={postTransformsResponse.metadata}
        />
      );
    }
    if (postTransformsBatchResponse) {
      if (!postTransformsBatchResponse.response[0].metadata.success) {
        return (
          <NormalText
            textContent={
              postTransformsBatchResponse.response[0].metadata.reason
            }
          />
        );
      }
      return (
        <Visualization
          afterImageUrls={
            postTransformsBatchResponse.response[0].presigned_urls
          }
          procedureDisplayMessage={adjustTextMap[selectedProcedure]}
          metaData={postTransformsBatchResponse.response[0].metadata}
        />
      );
    }
  };

  if (errorMessage) return <NormalText textContent={errorMessage} />;
  if (selectedProcedure === null) return null;

  return (
    <div className="screen-container">
      <TitleBar
        name={"Post Op Visualization"}
        disableBackButton={
          selectedProcedure &&
          !postTransformsResponse &&
          !postTransformsBatchResponse &&
          isAISupportedProcedure(selectedProcedure)
        }
        onClickBackButton={() => setScreen(TScreen.SELECT_MODEL_SCREEN)}
      />
      {selectedProcedure && isAISupportedProcedure(selectedProcedure) ? (
        <div style={screenStyles.visualizationContainer}>
          {renderVisualization()}
        </div>
      ) : (
        <div style={{ display: "flex", flex: 1 }}>
          <NormalText
            textContent={`${formatProcedureName(
              selectedProcedure,
            )} is not supported by AI.`}
          />
        </div>
      )}
      {showTryAnotherModelButton && (
        <PrimaryButton
          disabled={
            selectedProcedure &&
            !postTransformsResponse &&
            !postTransformsBatchResponse &&
            isAISupportedProcedure(selectedProcedure)
          }
          onClick={() => {
            setScreen(TScreen.SELECT_MODEL_SCREEN);
            handleSaveImageUrlOrBlob(null);
          }}
          fullWidth={true}
          text={"Try on another model"}
        />
      )}
      <Modal isOpen={isModalOpen}>
        <ModalContent
          batchId={batchId}
          selectedProcedure={selectedProcedure}
          handleCloseModal={() => {
            setIsModalOpen(false);
            mixpanelTrack("Form Submitted", [
              {
                propertyName: "Clinic Name",
                propertyValue:
                  clinic?.clinic_name ?? "failed to get clinic name",
              },
            ]);
            callFormSubmissionTracking(session, TFormName.ONBOARDING_TIER1);
          }}
          handleSetErrorMessage={handleSetErrorMessage}
          handleSetIsFormSubmittedTrue={handleSetIsFormSubmittedTrue}
          formName={FormNameType.ONBOARDING_TIER1}
          uploadType={uploadType}
        />
      </Modal>
    </div>
  );
};

const screenStyles = {
  visualizationContainer: {
    width: "80%",
    height: "100%",
    margin: "auto",
  },
  loading: {
    margin: "50px auto",
  },
  errorText: {
    color: "red",
    textAlign: "center",
    margin: "50px auto",
  },
} as const;

export default ScreenVisualize;
